// Words for campaigns, by season and by the job each post, Story, email or
// text does. Several lines for each job, so no two things in a campaign say
// the same, and two salons rarely match.
//
// Nothing here is a fact about a salon: no prices, no times, no names, no
// reviews. Those come from the salon's own records, or the job is skipped.
// {event} is the day or season ("Halloween", "party season"); {salon} is the
// salon's name on posts (emails and texts use {{SalonName}}). *word* is the
// design's accent. British spelling, no em or en dashes.

const any = {
  open: [
    { eyebrow: '{Event}', headline: '{Event} at *{salon}*.', body: 'Book in for {event} while there’s plenty of choice.' },
    { eyebrow: 'Now booking', headline: 'Ready for *{event}*?', body: 'Pick a time that suits you. It takes a minute online.' },
    { eyebrow: '{Event}', headline: 'Time for *you*.', body: 'Book your {event} appointment online, link in bio.' },
  ],
  lastcall: [
    { eyebrow: 'Last few days', headline: 'Book before *{event}*.', body: 'Times go quickly now. Link in bio.' },
    { eyebrow: 'Don’t leave it late', headline: 'Still time for *{event}*.', body: 'Book online in a minute.' },
  ],
  close: [
    { eyebrow: '{Event}', headline: 'Happy *{event}*.', body: 'From all of us at {salon}.' },
  ],
  team: [
    { eyebrow: 'Meet the team', line: 'Book in with {name} for {event}.' },
    { eyebrow: 'In the chair', line: '{name} is on the team at {salon}. Book online, link in bio.' },
  ],
  prove: [
    { headline: 'In their *words*.' },
    { headline: 'Kind *words*.' },
  ],
  // Over a photo of the salon's own work
  show: [
    { eyebrow: '{Event} at {salon}', headline: 'Fresh from *the chair*.' },
    { eyebrow: '{Event}', headline: 'Ready for *{event}*.' },
    { eyebrow: 'Recent work', headline: 'Made at *{salon}*.' },
  ],
  offer: [
    { eyebrow: 'Free before {event}', headline: 'Times for *you*.' },
    { eyebrow: '{Event}', headline: 'Chairs *free*.' },
  ],
  help: {
    any: [
      { label: 'Book ahead', headline: 'Beat the *rush*.', body: 'The weeks before {event} fill fastest. Booking early gets you the day and time you want.' },
    ],
  },
  email: {
    open: { subject: '{Event} at {{SalonName}}', preheader: 'Book early for the best times.', eyebrow: '{Event}', headline: 'Ready for *{event}*?', body: 'Book in for {event} while there’s plenty of choice. It takes a minute online.', button: 'Book now' },
    mid: { subject: 'Still time before {event}', preheader: 'The best times go first.', eyebrow: '{Event}', headline: 'Still *time*.', body: 'There are times left before {event}, but they’re going. Book the one that suits you.', button: 'Book now' },
    last: { subject: 'Last few days before {event}', preheader: 'Book online in a minute.', eyebrow: 'Last few days', headline: 'Don’t leave it *late*.', body: '{Event} is almost here. Book now for a time that suits you.', button: 'Book now' },
  },
  text: 'Hi {{FirstName}}, {event} is {when}. Book your time at {{SalonName}}: {{BookingLink}}',
  // The first of two texts, weeks before the last call
  textEarly: 'Hi {{FirstName}}, now booking for {event} at {{SalonName}}. Book early for the best times: {{BookingLink}}',
}

// A season (autumn, spring, the summer holidays) is here rather than coming,
// so its emails and text say so, without leaning on its name mid-sentence
const season = {
  email: {
    open: { subject: '{Event} at {{SalonName}}', preheader: 'A new season, a fresh look.', eyebrow: '{Event}', headline: 'A new season, *a fresh look*.', body: 'A change of season is a lovely time for something new. Book online in a minute.', button: 'Book now' },
    mid: { subject: 'Time for a change at {{SalonName}}?', preheader: 'Good times are still free.', eyebrow: '{Event}', headline: 'Time for a *change*?', body: 'There are still good times free. Book the one that suits you, online in a minute.', button: 'Book now' },
    last: { subject: 'Still time for a change at {{SalonName}}', preheader: 'Book online in a minute.', eyebrow: '{Event}', headline: 'Still time for *you*.', body: 'Book the time that suits you, online in a minute.', button: 'Book now' },
  },
  text: 'Hi {{FirstName}}, time for a change this season? Book at {{SalonName}}: {{BookingLink}}',
  help: [
    { label: 'Between visits', headline: 'Ask about *aftercare*.', body: 'Ask which products suit you at your next visit. The right ones at home make a real difference.' },
  ],
}

// How gift vouchers work, for any gifting season (true of Beu's vouchers)
const voucherHelp = [
  { label: 'How it works', headline: 'Gifted in *three taps*.', body: 'Choose an amount, pay online, and the voucher arrives by email in seconds, ready to print or forward.' },
  { label: 'Last-minute gift?', headline: 'Sorted in a *minute*.', body: 'Our gift vouchers arrive by email straight away, so it’s never too late.' },
]
const gift = (event, extra = {}) => ({
  open: [
    { eyebrow: event, headline: 'The gift they’ll *actually use*.', body: 'Gift vouchers from {salon}, sent by email in seconds.' },
    { eyebrow: event, headline: 'Give them *time for them*.', body: 'A {salon} gift voucher, bought online.' },
  ],
  lastcall: [
    { eyebrow: 'Last-minute gift', headline: 'Still time to *gift*.', body: 'Vouchers arrive by email in seconds. Link in bio.' },
    { eyebrow: 'Nearly {event}', headline: 'The gift that’s *never late*.', body: 'Order online and it’s in their inbox straight away.' },
  ],
  help: { any: voucherHelp },
  offer: [{ eyebrow: '{Event}', headline: 'A gift of *time*.' }],
  show: [
    { eyebrow: '{Event}', headline: 'A gift that looks *this good*.' },
    { eyebrow: 'Gift vouchers', headline: 'Give them *this*.' },
  ],
  email: {
    open: { subject: '{Event}: the gift sorted in a minute', preheader: 'Gift vouchers from {{SalonName}}, delivered by email.', eyebrow: '{Event}', headline: 'The gift they’ll *actually use*.', body: 'A gift voucher from {{SalonName}} is never the wrong size, and it arrives by email in seconds.', button: 'Buy a gift voucher' },
    mid: { subject: 'Still looking for a {event} gift?', preheader: 'Sorted in a minute, delivered by email.', eyebrow: '{Event}', headline: 'Still looking for *the gift*?', body: 'Choose an amount and it’s in their inbox in seconds, ready to print or forward.', button: 'Buy a gift voucher' },
    last: { subject: 'Last days to gift for {event}', preheader: 'Vouchers arrive by email in seconds.', eyebrow: 'Last few days', headline: 'The gift that’s *never late*.', body: '{Event} is {when}. A {{SalonName}} gift voucher arrives by email straight away.', button: 'Buy a gift voucher' },
  },
  text: 'Hi {{FirstName}}, {event} is {when}. A {{SalonName}} gift voucher arrives by email in seconds: {{GiftCardLink}}',
  textEarly: 'Hi {{FirstName}}, {event} gifts, sorted in a minute: a {{SalonName}} gift voucher, sent by email: {{GiftCardLink}}',
  ...extra,
  isGift: true,
})

const BANK = {
  autumn: {
    open: [
      { eyebrow: 'Autumn refresh', headline: 'New season, *new colour*.', body: 'Warm tones, fresh cuts and a little care after the summer sun.' },
      { eyebrow: 'Autumn at {salon}', headline: 'Hello, *autumn*.', body: 'Time for a change of season. Book online, link in bio.' },
      { eyebrow: 'Autumn refresh', headline: 'Cosy season, *fresh look*.', body: 'Book your autumn refresh while there’s plenty of choice.' },
    ],
    help: {
      hair: [{ label: 'After the summer', headline: 'Bring back the *shine*.', body: 'Sun, salt and chlorine leave hair dry. A treatment and a trim of the driest ends make the biggest difference.' }],
      barber: [{ label: 'Colder days', headline: 'Look after the *beard*.', body: 'Cold air dries the skin under a beard. A few drops of beard oil each morning keep both soft.' }],
      nails: [{ label: 'Colder days', headline: 'Hands need *more now*.', body: 'Swap to a richer hand cream as it gets colder, and your nails and cuticles will hold up far better.' }],
      lashes: [{ label: 'Colder days', headline: 'Heat is *no friend*.', body: 'Keep car heaters and hairdryers off your face. Hot, dry air makes lash glue brittle.' }],
      skin: [{ label: 'Colder days', headline: 'Richer *for winter*.', body: 'Swap to a richer moisturiser as it gets colder. Skin loses water faster in heated rooms.' }],
    },
    close: [],
    text: 'Hi {{FirstName}}, autumn is here. Time for a refresh at {{SalonName}}? Book online: {{BookingLink}}',
    email: {
      open: { subject: 'Autumn at {{SalonName}}', preheader: 'New season, new look.', eyebrow: 'Autumn refresh', headline: 'New season, *new colour*.', body: 'Autumn is here. Book in for a refresh after the summer, online in a minute.', button: 'Book now' },
    },
  },
  halloween: {
    open: [
      { eyebrow: 'Halloween', headline: 'Spooky season is *here*.', body: 'Something a little spooky for the next two weeks. Book online, link in bio.' },
      { eyebrow: 'Halloween at {salon}', headline: 'Something a little *spooky*.', body: 'Book in before the parties start.' },
      { eyebrow: 'Halloween', headline: 'Trick or *treat yourself*.', body: 'Book your Halloween appointment online.' },
    ],
    help: {
      hair: [{ label: 'After the party', headline: 'Spray colour, *out properly*.', body: 'Brush temporary colour through before you wash, then shampoo twice. It lifts out far more easily.' }],
      barber: [{ label: 'After the party', headline: 'Spray colour, *out properly*.', body: 'Two gentle shampoos and a good rinse until the water runs clear, rather than one hard scrub.' }],
      beauty: [{ label: 'After the party', headline: 'Costume make-up, *off gently*.', body: 'Melt heavy make-up with an oil cleanser first, then your usual wash. Your skin will thank you.' }],
    },
    close: [
      { eyebrow: 'Halloween', headline: 'Happy *Halloween*.', body: 'From all of us at {salon}.' },
    ],
    lastcall: [
      { eyebrow: 'Nearly Halloween', headline: 'The last few *spooky* days.', body: 'Book before the weekend. Link in bio.' },
    ],
    email: {
      open: { subject: 'Something a little spooky at {{SalonName}}', preheader: 'Book in before the parties start.', eyebrow: 'Halloween', headline: 'Spooky season is *here*.', body: 'Halloween is {when}. Book in before the parties start, online in a minute.', button: 'Book now' },
    },
  },
  bonfire: {
    open: [
      { eyebrow: 'Bonfire Night', headline: 'Sparkle for *bonfire night*.', body: 'Wrap up warm and look great doing it.' },
    ],
    help: {
      hair: [{ label: 'By the fire', headline: 'Tuck it *under a hat*.', body: 'Smoke and sparks cling to loose hair. A hat keeps it fresh for the next day.' }],
      any: [{ label: 'Bonfire Night', headline: 'Wrap up *warm*.', body: 'Enjoy the fireworks, and stay safe by the fire.' }],
    },
    close: [{ eyebrow: 'Bonfire Night', headline: 'Enjoy the *fireworks*.', body: 'From all of us at {salon}.' }],
  },
  movember: {
    open: [
      { eyebrow: 'Movember', headline: 'Grow it. *We’ll shape it.*', body: 'Growing for Movember? Book in when it needs a tidy.' },
      { eyebrow: 'Movember', headline: 'Mo *season*.', body: 'Book a shape-up as it comes in.' },
    ],
    help: {
      barber: [{ label: 'Growing a mo', headline: 'Leave it *two weeks*.', body: 'Let it grow for the first two weeks before shaping. It’s easier to see what it wants to do.' }],
    },
    text: 'Hi {{FirstName}}, growing for Movember? Book a shape-up at {{SalonName}}: {{BookingLink}}',
  },
  'black-friday': gift('Black Friday', {
    close: [],
    open: [
      { eyebrow: 'Black Friday', headline: 'Vouchers for *everyone* on your list.', body: 'Sort the gifts early with {salon} gift vouchers.' },
      { eyebrow: 'Black Friday', headline: 'Get the gifts *done*.', body: 'Gift vouchers, bought online and sent by email.' },
    ],
  }),
  'christmas-vouchers': gift('Christmas', {
    open: [
      { eyebrow: 'Christmas', headline: 'The easiest gift *this Christmas*.', body: 'Gift vouchers from {salon}, sent by email in seconds.' },
      { eyebrow: 'Christmas at {salon}', headline: 'Give them *time for them*.', body: 'A gift voucher for someone who deserves a treat.' },
      { eyebrow: 'Christmas', headline: 'Wrapped in *seconds*.', body: 'Buy a gift voucher online. It arrives by email, ready to forward.' },
    ],
    close: [{ eyebrow: 'Christmas', headline: 'Merry *Christmas*.', body: 'From all of us at {salon}.' }],
  }),
  'party-season': {
    open: [
      { eyebrow: 'Party season', headline: 'Party season is *here*.', body: 'December fills up fast. Book your party look early.' },
      { eyebrow: 'Party season', headline: 'Ready for the *office party*?', body: 'Book your time before the diary fills up.' },
    ],
    help: {
      hair: [{ label: 'Party tip', headline: 'Book it for the *afternoon*.', body: 'A blow-dry booked the afternoon of the party still looks fresh at midnight.' }],
      barber: [{ label: 'Party tip', headline: 'Trim a few days *before*.', body: 'Book your cut two or three days before the party, so the lines have softened just enough.' }],
      nails: [{ label: 'Party tip', headline: 'Nails two days *before*.', body: 'Book nails two or three days before the party, so they’re settled but still perfect.' }],
      lashes: [{ label: 'Party tip', headline: 'Lashes three days *before*.', body: 'Book lashes three or four days before the party, so they’ve settled and look their fullest on the night.' }],
      skin: [{ label: 'Party tip', headline: 'A facial a week *before*.', body: 'Book a facial about a week before the party, so any redness has calmed and your skin is glowing.' }],
      beauty: [{ label: 'Party tip', headline: 'Book a few days *before*.', body: 'Book in a few days before the party, so everything has settled and looks its best on the night.' }],
    },
    close: [],
    lastcall: [
      { eyebrow: 'Last few times', headline: 'The last party *times*.', body: 'December is nearly full. Link in bio.' },
    ],
    email: {
      mid: { subject: 'December is filling up at {{SalonName}}', preheader: 'Book your party look before it’s gone.', eyebrow: 'Party season', headline: 'December is *filling up*.', body: 'There are still times before the parties, but they’re going. Book the one that suits you.', button: 'Book now' },
      last: { subject: 'The last party times at {{SalonName}}', preheader: 'Book online in a minute.', eyebrow: 'Party season', headline: 'The last party *times*.', body: 'December is nearly full. Book now for a time that suits you.', button: 'Book now' },
    },
    text: 'Hi {{FirstName}}, December is filling up fast. Book your party look at {{SalonName}}: {{BookingLink}}',
  },
  nye: {
    open: [
      { eyebrow: 'New Year’s Eve', headline: 'End the year *looking good*.', body: 'Book your New Year’s Eve look before the diary fills.' },
      { eyebrow: 'New Year’s Eve', headline: 'One last *treat*.', body: 'The last times of the year. Link in bio.' },
    ],
    help: {
      hair: [{ label: 'Make it last', headline: 'Up all *night*.', body: 'Ask for a finish that holds: a little texture spray on dry hair keeps a style going past midnight.' }],
      any: [{ label: 'New Year’s Eve', headline: 'Book the *afternoon*.', body: 'Afternoon appointments on New Year’s Eve go first. Book early for the time you want.' }],
    },
    close: [{ eyebrow: 'New Year’s Eve', headline: 'Happy *New Year*.', body: 'Thank you for this year, from all of us at {salon}.' }],
  },
  'new-year': {
    open: [
      { eyebrow: 'New year', headline: 'New year, *new look*.', body: 'January chairs are open. Book online, link in bio.' },
      { eyebrow: 'January at {salon}', headline: 'A fresh *start*.', body: 'Time for something new. Book in this month.' },
    ],
    help: {
      hair: [{ label: 'Winter hair', headline: 'Heating *dries hair*.', body: 'Central heating and cold air dry hair out. A weekly mask and a lower heat setting make a real difference.' }],
      barber: [{ label: 'Winter beards', headline: 'Keep it *soft*.', body: 'Heating dries a beard out. A daily beard oil keeps it soft and stops the itch.' }],
      nails: [{ label: 'Winter nails', headline: 'Oil every *night*.', body: 'Heating dries cuticles. A drop of cuticle oil each night keeps nails flexible and less likely to split.' }],
      lashes: [{ label: 'Winter lashes', headline: 'Brush them *through*.', body: 'Scarves and hats tangle lashes. A clean spoolie each morning keeps them lying right.' }],
      skin: [{ label: 'Winter skin', headline: 'SPF, even *now*.', body: 'Winter sun still ages skin. A daily SPF is the one habit that shows in five years.' }],
    },
    close: [],
    text: 'Hi {{FirstName}}, happy new year! January chairs are open at {{SalonName}}: {{BookingLink}}',
    email: {
      open: { subject: 'New year at {{SalonName}}', preheader: 'January chairs are open.', eyebrow: 'New year', headline: 'A fresh *start*.', body: 'January is a lovely time to try something new. Book online in a minute.', button: 'Book now' },
    },
  },
  valentines: gift('Valentine’s Day', {
    open: [
      { eyebrow: 'Valentine’s Day', headline: 'Better than *flowers*.', body: 'A {salon} gift voucher, sent by email in seconds.' },
      { eyebrow: 'Valentine’s Day', headline: 'Show some *love*.', body: 'Treat someone to time at {salon}.' },
    ],
  }),
  'mothers-day': gift('Mother’s Day', {
    open: [
      { eyebrow: 'Mother’s Day', headline: 'Treat Mum to *time for her*.', body: 'A {salon} gift voucher, sent by email in seconds.' },
      { eyebrow: 'Mother’s Day', headline: 'For the one who *does it all*.', body: 'Gift vouchers, bought online.' },
    ],
  }),
  'fathers-day': gift('Father’s Day', {
    open: [
      { eyebrow: 'Father’s Day', headline: 'A gift Dad will *actually use*.', body: 'A {salon} gift voucher, sent by email in seconds.' },
      { eyebrow: 'Father’s Day', headline: 'Sorted for *Dad*.', body: 'Gift vouchers, bought online in a minute.' },
    ],
  }),
  'st-patricks': {
    open: [
      { eyebrow: 'St Patrick’s Day', headline: 'Ready for the *celebrations*?', body: 'Book in before St Patrick’s Day.' },
    ],
    close: [{ eyebrow: 'St Patrick’s Day', headline: 'Happy *St Patrick’s Day*.', body: 'From all of us at {salon}.' }],
  },
  spring: {
    open: [
      { eyebrow: 'Spring refresh', headline: 'Fresh for *spring*.', body: 'Lighter days, lighter looks. Book your spring refresh.' },
      { eyebrow: 'Spring at {salon}', headline: 'Hello, *spring*.', body: 'Book in for a change of season.' },
    ],
    help: {
      hair: [{ label: 'Going lighter?', headline: 'Start with a *chat*.', body: 'Going lighter for spring can take more than one visit. A consultation first means no surprises.' }],
      beauty: [{ label: 'Spring skin', headline: 'Lift the *winter dullness*.', body: 'A gentle exfoliation once a week brings skin back to life after winter.' }],
      any: [{ label: 'Spring', headline: 'A *fresh start*.', body: 'Longer days are a lovely time to try something new.' }],
    },
    close: [],
    text: 'Hi {{FirstName}}, spring is here. Book your spring refresh at {{SalonName}}: {{BookingLink}}',
  },
  easter: {
    open: [
      { eyebrow: 'Easter', headline: 'Ready for the *long weekend*?', body: 'Book before Easter while there’s plenty of choice.' },
      { eyebrow: 'Easter at {salon}', headline: 'Easter, *sorted*.', body: 'Book online, link in bio.' },
    ],
    help: { any: [{ label: 'Easter weekend', headline: 'Book *early*.', body: 'The days before a long weekend fill first. Book now for the time you want.' }] },
    close: [{ eyebrow: 'Easter', headline: 'Happy *Easter*.', body: 'From all of us at {salon}.' }],
  },
  weddings: {
    open: [
      { eyebrow: 'Wedding season', headline: 'Bridal, *booked in*.', body: 'Trials, the big day and everyone in the party.' },
      { eyebrow: 'Wedding season', headline: 'Ready for *the day*.', body: 'Book your trial early. Link in bio.' },
    ],
    help: { any: [{ label: 'Planning ahead', headline: 'Trial *six weeks* before.', body: 'Book a trial six to eight weeks before the day, so there’s time to perfect the look.' }] },
    close: [],
    text: 'Hi {{FirstName}}, getting married this year? Book your trial early at {{SalonName}}: {{BookingLink}}',
  },
  prom: {
    open: [
      { eyebrow: 'Prom season', headline: 'Prom, *sorted early*.', body: 'Prom dates fill fast. Book yours early.' },
      { eyebrow: 'Prom season', headline: 'The big *night*.', body: 'Book your prom look online, link in bio.' },
    ],
    help: { any: [{ label: 'Prom tip', headline: 'Bring a *photo*.', body: 'A photo of the outfit helps us match the look to it.' }] },
    close: [],
    lastcall: [
      { eyebrow: 'Prom season', headline: 'Prom dates are *filling up*.', body: 'Book yours now. Link in bio.' },
    ],
    email: {
      open: { subject: 'Prom season at {{SalonName}}', preheader: 'Prom dates fill fast. Book yours early.', eyebrow: 'Prom season', headline: 'Prom, *sorted early*.', body: 'Prom dates fill fast. Book your prom look early for the time you want.', button: 'Book now' },
      mid: { subject: 'Prom dates are filling up', preheader: 'Book yours before they go.', eyebrow: 'Prom season', headline: 'Prom dates are *filling up*.', body: 'Times around the big proms go first. Book yours now.', button: 'Book now' },
      last: { subject: 'Still time to book for prom', preheader: 'Book online in a minute.', eyebrow: 'Prom season', headline: 'Still time for *prom*.', body: 'Book your prom look now for a time that suits you.', button: 'Book now' },
    },
    text: 'Hi {{FirstName}}, prom dates are filling up. Book your prom look at {{SalonName}}: {{BookingLink}}',
    textEarly: 'Hi {{FirstName}}, prom dates fill fast. Book your prom look early at {{SalonName}}: {{BookingLink}}',
  },
  'summer-start': {
    open: [
      { eyebrow: 'Summer ready', headline: 'Summer *ready*.', body: 'Book in before you go away.' },
      { eyebrow: 'Summer at {salon}', headline: 'Hello, *summer*.', body: 'Light, easy looks for sunny days. Book online.' },
    ],
    help: {
      hair: [{ label: 'Holiday hair', headline: 'Rinse after *every swim*.', body: 'Rinse out chlorine and salt water after swimming, and use a leave-in that protects from the sun.' }],
      barber: [{ label: 'Before you go', headline: 'Trim a week *before*.', body: 'Book your cut a week before you fly, so it has settled in time for the photos.' }],
      beauty: [{ label: 'Before you go', headline: 'A few days *before*.', body: 'Book nails and lashes a few days before you travel, so they’re settled for the trip.' }],
    },
    close: [],
    text: 'Hi {{FirstName}}, going away this summer? Book in before you go at {{SalonName}}: {{BookingLink}}',
  },
  summer: {
    open: [
      { eyebrow: 'Holiday ready', headline: 'Holiday *ready*.', body: 'The summer holidays are here. Book in before you go.' },
      { eyebrow: 'Summer holidays', headline: 'Sun’s *out*.', body: 'Easy looks for the holidays. Link in bio.' },
    ],
    help: {
      hair: [{ label: 'Holiday hair', headline: 'Protect it in the *sun*.', body: 'A hat or a leave-in with sun protection keeps colour from fading on holiday.' }],
      any: [{ label: 'Before you go', headline: 'Book a week *ahead*.', body: 'Book in a week before you go, so everything has settled before the photos start.' }],
    },
    close: [],
    text: 'Hi {{FirstName}}, the summer holidays are here. Book in before you go at {{SalonName}}: {{BookingLink}}',
  },
  'back-to-school': {
    open: [
      { eyebrow: 'Back to school', headline: 'Ready for *the new term*.', body: 'Get the kids booked in before term starts.' },
      { eyebrow: 'Back to school', headline: 'First-day *fresh*.', body: 'The last week of the holidays fills fast. Book early.' },
    ],
    help: { any: [{ label: 'Before term', headline: 'Book *early*.', body: 'The last week of the summer holidays is the busiest. Book the kids in early for the time you want.' }] },
    close: [],
    text: 'Hi {{FirstName}}, the new term is nearly here. Book the kids in at {{SalonName}}: {{BookingLink}}',
  },
}

const cap = (s) => String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1)

// Fill in the placeholders in a line (a string or an object of strings)
export const fill = (value, vars) => {
  if (typeof value === 'string') {
    return value
      .replace(/\{Event\}/g, cap(vars.event))
      .replace(/\{event\}/g, vars.event || '')
      .replace(/\{salon\}/g, vars.salon || 'us')
      .replace(/\{name\}/g, vars.name || '')
      .replace(/\{when\}/g, vars.when || 'soon')
  }
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((k) => { out[k] = fill(value[k], vars) })
    return out
  }
  return value
}

// A moment's own words. A gifting season whose salon has no vouchers on sale
// keeps only its greeting ("Merry Christmas"): every other line sells vouchers
const ownWords = (momentId, gifting) => {
  const own = BANK[momentId] || {}
  return own.isGift && !gifting ? { close: own.close } : own
}

// A job's lines for a moment: its own first, then the ones any moment can
// use. An empty list of its own means the job doesn't suit it ("Happy party
// season" reads wrong), so there are none.
export const linesFor = (momentId, role, trade = 'hair', { gifting = false, isSeason = false, specialism = null } = {}) => {
  const own = ownWords(momentId, gifting)
  const base = gifting && !own.isGift ? gift('{Event}') : any
  if (role === 'help') {
    const h = own.help || {}
    // A beauty business gets its specialism's tips (nails, lashes, skin),
    // then the ones that suit any beauty business
    const mine = trade === 'beauty' ? [...((specialism && h[specialism]) || []), ...(h.beauty || [])] : (h[trade] || [])
    return [...mine, ...(h.any || []), ...(gifting ? voucherHelp : []), ...(isSeason ? season.help : any.help.any)]
  }
  if (role === 'show' && gifting) return [...(own.show || []), ...gift('{Event}').show, ...any.show]
  if (role === 'email' || role === 'text') return null
  if (Array.isArray(own[role]) && !own[role].length) return []
  return [...(own[role] || []), ...(base[role] || any[role] || [])]
}

// A moment's email words for a stage ('open', 'mid', 'last')
export const emailWords = (momentId, stage, { gifting = false, isSeason = false } = {}) => {
  const own = ownWords(momentId, gifting).email || {}
  const base = gifting ? gift('{Event}').email : isSeason ? season.email : any.email
  return own[stage] || base[stage]
}

export const textWords = (momentId, { gifting = false, isSeason = false, early = false } = {}) => {
  const own = ownWords(momentId, gifting)
  const base = gifting ? gift('{Event}') : isSeason ? season : any
  return early ? (own.textEarly || base.textEarly || any.textEarly) : (own.text || base.text)
}

// The salon's voice (its soul from the front desk): polished drops the
// exclamation marks; quick keeps a body to its first sentence
export const inTone = (value, tone) => {
  if (!tone || typeof value !== 'string') return value
  if (tone === 'polished') return value.replace(/!/g, '.')
  if (tone === 'quick') { const i = value.indexOf('. '); return i === -1 ? value : value.slice(0, i + 1) } // no lookbehind: older iOS can't parse it
  return value
}
